import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

/**
 * Bundle 2 : plafond d’unités « cure » (_cure: true) selon la variante du pack (flacons).
 * Budget : _cure_flacon_budget sur la ligne pack (target_quantity / metafield) ; repli JS si ancienne ligne sans propriété.
 *
 * Perf : si somme des qty flacons _cure ≤ budget après un seul GET /cart.js → sortie immédiate (pas de POST).
 * Les propriétés ne « corrigent » pas les quantités : elles portent le plafond (_cure_flacon_budget) et les marqueurs (_cure_scope).
 */
const VARIANT_FLACONS = {
  '56961320780108': null,
  '56961320812876': 3,
};

/** Rétrocompat : lignes sans _cure_scope (anciens paniers). Les nouvelles lignes huiles ont _cure_scope: flacon. */
const DISCOUNTABLE_PRODUCT_IDS = new Set(['15646849958220']);

const fetchCartJson = async () => {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  return response.json();
};

const isCureLine = (properties) => {
  if (!properties || typeof properties !== 'object') return false;
  return String(properties._cure) === 'true';
};

const getBudgetForPackLine = (variantIdStr, packLine) => {
  const props = packLine.properties || {};
  const raw = props._cure_flacon_budget ?? props._cure_budget;
  const fromProperty = parseInt(String(raw), 10);
  if (Number.isFinite(fromProperty) && fromProperty > 0) return fromProperty;

  const fixed = VARIANT_FLACONS[variantIdStr];
  if (fixed != null) return fixed;

  return null;
};

const findPackContext = (cart) => {
  if (!cart?.items?.length) return null;

  for (const item of cart.items) {
    const vid = String(item.variant_id ?? item.id);
    const props = item.properties || {};
    const inVariantAllowlist = Object.prototype.hasOwnProperty.call(VARIANT_FLACONS, vid);
    const fromBundleMotherLine =
      String(props._bundle_builder) === 'true' &&
      props._cure_flacon_budget != null &&
      String(props._cure_flacon_budget).trim() !== '';

    if (!inVariantAllowlist && !fromBundleMotherLine) continue;

    const budget = getBudgetForPackLine(vid, item);
    if (budget == null || budget <= 0) continue;

    return { packVariantId: vid, budget };
  }

  return null;
};

const isFlaconBudgetLine = (item) => {
  const props = item.properties || {};
  if (!isCureLine(props)) return false;

  if (String(props._cure_scope) === 'pack_addon') return false;

  if (String(props._cure_scope) === 'flacon') return true;

  return DISCOUNTABLE_PRODUCT_IDS.has(String(item.product_id ?? ''));
};

const getCureDiscountableLines = (cart) => {
  return cart.items
    .map((item, index) => {
      const line = index + 1;
      return { ...item, line };
    })
    .filter((item) => isFlaconBudgetLine(item));
};

const sumQuantities = (lines) => lines.reduce((acc, line) => acc + (parseInt(line.quantity, 10) || 0), 0);

const getSectionPayload = () => {
  const drawer = document.querySelector('cart-drawer');
  const sectionId = drawer?.dataset?.sectionId;
  if (!sectionId) return {};

  return {
    sections: sectionId,
    sections_url: window.location.pathname,
  };
};

const postCartChange = async (line, quantity) => {
  const body = JSON.stringify({
    line,
    quantity,
    ...getSectionPayload(),
  });

  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body,
  });

  const text = await response.text();
  return JSON.parse(text);
};

const postCartAdd = async (variantId, quantity) => {
  const body = JSON.stringify({
    items: [{ id: variantId, quantity, properties: {} }],
    ...getSectionPayload(),
  });

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body,
  });

  return response.json();
};

const dispatchDrawerRefresh = (sections, itemCount) => {
  document.dispatchEvent(
    new CartUpdatedEvent(
      {},
      {
        source: 'bundle-cure-budget',
        itemCount,
        sections,
      },
    ),
  );
};

/**
 * File unique : chaque passage attend la fin du précédent (quantité + checkout).
 */
let reconcileChain = Promise.resolve();

const setCheckoutCtasDisabled = (disabled) => {
  for (const button of document.querySelectorAll('[data-ref="checkout-button"]')) {
    if (!(button instanceof HTMLButtonElement)) continue;

    if (disabled) {
      if (!button.disabled) {
        button.dataset.bundleCureBudgetUnlock = 'true';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      continue;
    }

    if (button.dataset.bundleCureBudgetUnlock === 'true') {
      delete button.dataset.bundleCureBudgetUnlock;
      button.disabled = false;
    }
    button.setAttribute('aria-busy', 'false');
  }
};

const runReconcileOnce = async () => {
  let cart = await fetchCartJson();
  if (!cart?.items?.length) return;

  const ctx = findPackContext(cart);
  if (!ctx) return;

  const { budget } = ctx;
  let cureLines = getCureDiscountableLines(cart);
  let totalCure = sumQuantities(cureLines);

  if (totalCure <= budget) return;

  setCheckoutCtasDisabled(true);

  let lastDrawerSections = null;
  let lastItemCount = null;

  try {
    while (totalCure > budget) {
      const excess = totalCure - budget;
      cureLines.sort((a, b) => b.line - a.line);
      const target = cureLines[0];
      if (!target) break;

      const take = Math.min(parseInt(target.quantity, 10) || 0, excess);
      if (take <= 0) break;

      const newQty = (parseInt(target.quantity, 10) || 0) - take;
      const variantId = target.variant_id ?? target.id;

      const changeResult = await postCartChange(target.line, newQty);

      if (changeResult.errors) {
        console.error('bundle-cure-budget: cart/change', changeResult.errors);
        break;
      }

      const addResult = await postCartAdd(variantId, take);

      if (addResult.status || addResult.message) {
        console.error('bundle-cure-budget: cart/add', addResult);
        break;
      }

      cart = await fetchCartJson();
      if (!cart?.items?.length) break;

      cureLines = getCureDiscountableLines(cart);
      totalCure = sumQuantities(cureLines);

      lastDrawerSections = addResult.sections ?? changeResult.sections;
      lastItemCount = addResult.item_count ?? changeResult.item_count ?? cart.item_count;
    }

    if (lastDrawerSections) {
      dispatchDrawerRefresh(lastDrawerSections, lastItemCount);
    }
  } finally {
    setCheckoutCtasDisabled(false);
  }
};

const scheduleReconcile = () => {
  reconcileChain = reconcileChain
    .then(() => runReconcileOnce())
    .catch((error) => {
      console.error('bundle-cure-budget:', error);
    });

  return reconcileChain;
};

/**
 * Attend la fin des corrections éventuelles (à appeler avant checkout).
 */
const ensureBundleCureBudget = async () => {
  await scheduleReconcile();
};

const onCartUpdated = (event) => {
  if (event?.detail?.data?.source === 'bundle-cure-budget') return;

  queueMicrotask(() => {
    scheduleReconcile();
  });
};

document.addEventListener(ThemeEvents.cartUpdated, onCartUpdated);

globalThis.ensureBundleCureBudget = ensureBundleCureBudget;

const themeGlobal = globalThis.Theme;
if (typeof themeGlobal === 'object' && themeGlobal !== null) {
  themeGlobal.ensureBundleCureBudget = ensureBundleCureBudget;
}
